import * as React from 'react';
import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/ui/lib/utils';

// shadcn（base-nova）の Badge を CLI で追加し、FDN UI Guideline の label に合わせて調整した。
// - default = Type=Required（Brand Color/FDN Blue 塗り・白文字）
// - secondary = Type=Optional（BG/Gray・FDN Text）。トークンは既存の muted / foreground と同じ値
// - 寸法は Figma の label（高さ 20px、余白 4px、文字 12px、line-height 1、Regular）。
//   生成元の rounded-4xl（ピル）は角丸の小さい Figma に合わせ rounded-md にした
//   （Figma 実測 border-radius: 3px。既存トークンのどちらとも一致しないため、
//   DS の基準角丸である rounded-md（4px）に丸める）
// - overlay はカード画像の上に置くラベル用（半透明の白）。配置（absolute 等）は利用側が持つ
// - destructive / ghost / link、[a]:hover、dark: は利用箇所が無いため入れていない
// - ring-[3px] は px リテラルのため ring-3 にした
// - 枠線色は base でなく variant 側に持たせている（塗りの variant は border-transparent、outline は border-border）。
//   base に置いてから variant で上書きする形より、各 variant が自分の枠線色を宣言する方が読みやすいため
const badgeVariants = cva(
  'tw:group/badge tw:box-border tw:inline-flex tw:h-5 tw:w-fit tw:shrink-0 tw:items-center tw:justify-center tw:gap-1 tw:overflow-hidden tw:rounded-md tw:border tw:px-1 tw:text-xs/none tw:font-normal tw:whitespace-nowrap tw:transition-colors tw:focus-visible:border-ring tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:[&>svg]:pointer-events-none tw:[&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'tw:border-transparent tw:bg-brand tw:text-brand-foreground',
        secondary: 'tw:border-transparent tw:bg-muted tw:text-foreground',
        outline: 'tw:border-border tw:text-foreground',
        overlay: 'tw:border-transparent tw:bg-background/80 tw:text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type BadgeProps = useRender.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants>;

// React 18 では ref が props として渡らないため forwardRef で包み、useRender の ref に渡す
// （Base UI の React 18 向け手順）。React 19 移行時に外す。
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, variant = 'default', render, ...props },
  forwardedRef,
) {
  return useRender({
    defaultTagName: 'span',
    ref: forwardedRef,
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props,
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  });
});
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
